/*
  ========================================================================
  THEME: Impact v7.0.1 merge target + Gro unified rebuild
  STORES: Gro Clinics AU (groclinics.com.au) + NZ (groclinics.co.nz)
  LAST UPDATED: 25/08/2026
  PREVIOUSLY: 15/06/2026
  ========================================================================
  Impact base-theme reference: the vendor URL in assets/documentation.txt.
  Gro design spec: 05-design/00-web-design-system (vault root relative).
  Custom Gro modules are documented in snippets/_gro-docs-*.liquid.
  ========================================================================

  gro-ahpra-terminology.js - rewrites four restricted surgical words in
  visible page text so a page cannot advertise a restricted title.

  OWNERSHIP
    Owns the word list, the case-preservation rule, the excluded-tag list and
    the MutationObserver that keeps the rewrite applied to content added after
    load.
    Does NOT own whether a page is allowed to use the restricted wording (that
    is the gro-page-config section), the script tag in layout/theme.liquid
    (that is the layout), or the source copy in any page, product, metaobject
    or module. This is a safety net over wrong stored copy, not a fix for it.

  GRO MODULE CONTRACT - read before editing
    1. terminologyPattern carries the /g flag and is shared across calls, so it
       keeps lastIndex between them. rewriteTextNode calls .test() and then
       resets terminologyPattern.lastIndex = 0 before .replace(). Deleting that
       reset makes the rewrite silently skip matches on alternate text nodes.
    2. The observer must never produce a replacement that can match again.
       Writing nodeValue fires a characterData mutation which re-enters
       rewriteTextNode; it only terminates because the replacement no longer
       matches. Adding a self-matching pair creates an infinite loop.
    3. rewriteSubtree receives mutation.addedNodes entries, which may be text
       nodes, elements or document fragments. Keep the nodeType branching.
    4. data-gro-terminology-ignore is the public opt-out hook for other
       modules. contenteditable elements are excluded so this never fights an
       editor. Do not narrow either check.
    5. The script is loaded with defer and corrects text after first paint.
       Do not make it render blocking to hide the brief flash; correct the
       source copy instead.
    6. Modern JS only: const/let, arrow functions, optional chaining. No var.
       No em dashes anywhere in this file.

  DEPENDENCIES
    layout/theme.liquid loads this asset inside
      {%- unless content_for_layout contains 'gro-allow-surgical-terms' -%}.
    sections/gro-page-config.liquid prints that marker into content_for_layout
      when its opt-in setting is on. The literal string is duplicated in three
      files; changing it requires all three.
    No snippets, assets, metaobjects, metafields or apps.

  DELIBERATE EXEMPTIONS
    AGENTS.md rule 3 "CSS over JS" - a text substitution across arbitrary
    rendered content cannot be expressed in CSS. JS is the only route.
    No other exemptions.
  ========================================================================
*/

(() => {
  const replacements = {
    surgery: 'procedure',
    surgeries: 'procedures',
    surgeon: 'doctor',
    surgeons: 'doctors'
  };
  const terminologyPattern = /\b(surgeries|surgery|surgeons|surgeon)\b/gi;
  const excludedTags = new Set([
    'CODE',
    'IFRAME',
    'KBD',
    'NOSCRIPT',
    'PRE',
    'SAMP',
    'SCRIPT',
    'STYLE',
    'TEMPLATE',
    'TEXTAREA'
  ]);

  const preserveCase = (matched, replacement) => {
    if (matched === matched.toUpperCase()) return replacement.toUpperCase();
    if (matched === matched.toLowerCase()) return replacement;

    if (matched[0] === matched[0].toUpperCase()) {
      return replacement[0].toUpperCase() + replacement.slice(1);
    }

    return replacement;
  };

  const isExcluded = (textNode) => {
    const parent = textNode.parentElement;

    return !parent ||
      excludedTags.has(parent.tagName) ||
      parent.closest('[data-gro-terminology-ignore], [contenteditable="true"], [contenteditable=""]');
  };

  const rewriteTextNode = (textNode) => {
    if (isExcluded(textNode) || !terminologyPattern.test(textNode.nodeValue)) return;

    terminologyPattern.lastIndex = 0;
    textNode.nodeValue = textNode.nodeValue.replace(terminologyPattern, (matched) => {
      return preserveCase(matched, replacements[matched.toLowerCase()]);
    });
  };

  const rewriteSubtree = (root) => {
    if (root.nodeType === Node.TEXT_NODE) {
      rewriteTextNode(root);
      return;
    }

    if (
      root.nodeType !== Node.ELEMENT_NODE &&
      root.nodeType !== Node.DOCUMENT_FRAGMENT_NODE
    ) {
      return;
    }

    if (
      root.nodeType === Node.ELEMENT_NODE &&
      (
        excludedTags.has(root.tagName) ||
        root.matches('[data-gro-terminology-ignore], [contenteditable="true"], [contenteditable=""]')
      )
    ) {
      return;
    }

    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
    let textNode = walker.nextNode();

    while (textNode) {
      rewriteTextNode(textNode);
      textNode = walker.nextNode();
    }
  };

  const start = () => {
    rewriteSubtree(document.body);

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'characterData') {
          rewriteTextNode(mutation.target);
          return;
        }

        mutation.addedNodes.forEach(rewriteSubtree);
      });
    });

    observer.observe(document.body, {
      childList: true,
      characterData: true,
      subtree: true
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
